import { useEffect, useRef, useState } from 'react';
import { fetchActive, getCached } from '../services/publicContent';

const FETCH_TIMEOUT_MS = 8000;

/**
 * usePublicCollection — loads an active public collection (services, gallery,
 * team) through the lite Firestore SDK.
 *
 * Replaces the near-identical listener + loading + error + timeout effect
 * that was copy-pasted across PriceList, Gallery and Team.
 *
 * Initial state comes from the react-snap snapshot when available, so
 * pre-rendered pages hydrate with content already on screen. Otherwise a
 * spinner shows until the fetch resolves (8 s timeout).
 *
 * @param {string} collectionName
 * @returns {{ items: Array, loading: boolean, error: Error|null }}
 */
export const usePublicCollection = (collectionName) => {
    // Read once — the preloaded snapshot never changes during a page's life.
    const preloadedRef = useRef(getCached(collectionName));
    const preloaded = preloadedRef.current;
    // Present-but-empty still counts as loaded, else the first render shows a
    // spinner where the pre-rendered HTML has an empty list.
    const hasPreloaded = Array.isArray(preloaded);
    const hasContent = hasPreloaded && preloaded.length > 0;

    const [items, setItems] = useState(preloaded ?? []);
    const [loading, setLoading] = useState(!hasPreloaded);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        let settled = false;

        const timer = setTimeout(() => {
            if (cancelled || settled) return;
            // Only surface a timeout when there is nothing on screen already.
            if (!hasContent) setError(new Error('timeout'));
            setLoading(false);
        }, FETCH_TIMEOUT_MS);

        fetchActive(collectionName)
            .then((data) => {
                settled = true;
                clearTimeout(timer);
                if (cancelled) return;
                setItems(data);
                setError(null);
                setLoading(false);
            })
            .catch((err) => {
                settled = true;
                clearTimeout(timer);
                if (cancelled) return;
                // Keep preloaded content visible if revalidation fails.
                if (!hasContent) setError(err);
                setLoading(false);
            });

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [collectionName, hasContent]);

    return { items, loading, error };
};
